/**
 * The GDI colour scale (brief §14).
 *
 * `0–20 unauffällig grau, 20–40 gelb, 40–60 orange, 60–80 rot, 80–100 magenta.`
 *
 * The property that makes it work for colour-blind readers is that it is **monotone in
 * lightness, not only in hue**: the relative luminance rises from step to step, so somebody
 * who cannot separate the orange from the red still reads the busier cell as the brighter
 * one. Reordering these entries or "brightening up" the grey breaks that quietly — the map
 * keeps working for most people and stops working for some.
 */

export type Stop = { at: number; colour: string; label: string };

export const GDI_STOPS: readonly Stop[] = [
  { at: 0, colour: '#4a4f57', label: 'nichts los' },
  { at: 20, colour: '#8b8f2f', label: 'ein bisschen' },
  { at: 40, colour: '#d8a018', label: 'ganz gut' },
  { at: 60, colour: '#e8622a', label: 'viel los' },
  { at: 80, colour: '#ff2d55', label: 'Hotspot' },
  { at: 100, colour: '#ff2fd0', label: 'Ausnahmezustand' },
];

/** The MapLibre `interpolate` expression for the scale, so the GPU does the work. */
export function gdiColourExpression(property = 'gdi'): unknown[] {
  return [
    'interpolate',
    ['linear'],
    ['get', property],
    ...GDI_STOPS.flatMap((stop) => [stop.at, stop.colour]),
  ];
}

export function gdiColour(gdi: number): string {
  const clamped = Math.min(100, Math.max(0, gdi));
  let previous = GDI_STOPS[0] as Stop;
  for (const stop of GDI_STOPS) {
    if (stop.at >= clamped) return clamped === stop.at ? stop.colour : previous.colour;
    previous = stop;
  }
  return previous.colour;
}

const channels = (hex: string): [number, number, number] => [
  Number.parseInt(hex.slice(1, 3), 16) / 255,
  Number.parseInt(hex.slice(3, 5), 16) / 255,
  Number.parseInt(hex.slice(5, 7), 16) / 255,
];

/**
 * The colour at a GDI exactly as the hexagon's fill has it — linear between the stops, which is
 * what `gdiColourExpression` asks MapLibre for — as `[r, g, b]` from 0 to 1, for a shader.
 *
 * Not `gdiColour`: that one names the band a cell is in, and a pulse in the band's colour
 * would be a different colour from the cell it lights up.
 */
export function gdiRgb(gdi: number): [number, number, number] {
  const clamped = Math.min(100, Math.max(0, gdi));
  let lower = GDI_STOPS[0] as Stop;
  for (const upper of GDI_STOPS) {
    if (upper.at >= clamped) {
      const t = upper.at === lower.at ? 0 : (clamped - lower.at) / (upper.at - lower.at);
      const [r0, g0, b0] = channels(lower.colour);
      const [r1, g1, b1] = channels(upper.colour);
      return [r0 + (r1 - r0) * t, g0 + (g1 - g0) * t, b0 + (b1 - b0) * t];
    }
    lower = upper;
  }
  return channels(lower.colour);
}

export function gdiLabel(gdi: number): string {
  let label = GDI_STOPS[0]?.label ?? '';
  for (const stop of GDI_STOPS) {
    if (gdi >= stop.at) label = stop.label;
  }
  return label;
}

/** The symbol per vibe mood (`vibeMood` in the domain), all of them, for the map to prepare. */
export const VIBE_SYMBOLS = { hot: '🔥', meh: '😐', lame: '💤' } as const;

/** Brief §9a.3: the vibe is a symbol on the hexagon, never a second colour ramp. */
export function vibeSymbol(vibe: number | null): string {
  if (vibe === null) return '';
  if (vibe >= 40) return VIBE_SYMBOLS.hot;
  if (vibe < -20) return VIBE_SYMBOLS.lame;
  return VIBE_SYMBOLS.meh;
}
